import React, { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { listarCruzamentos } from '../../../data/animals';
import { describeError } from '../../../data/errors';
import { ehTouro, ehDoadora, ehReceptora } from '../../../utils/animais';
import { fmt } from '../../../utils/workflowPDF';
import { MARK } from '../../../constants';
import JanelaModal from '../../JanelaModal';

/**
 * A FICHA DE CRUZAMENTOS DE UM ANIMAL.
 *
 * Nasceu de um pedido de comunicação à ABCZ: o veterinário passa "touro tal com
 * a receptora tal, nasceu faz três meses", e a comunicação pede o dia da
 * aspiração. Sem isto, achar o trabalho era revirar os serviços um por um.
 * Abre-se o touro, digita-se a receptora, e a linha traz as três datas.
 *
 * A coluna do próprio animal sai: na ficha do touro todas as linhas têm o mesmo
 * touro, e o que vale para toda linha não carrega informação. "Ambos" fica com
 * todas, porque ali a vaca aparece ora como doadora, ora como receptora.
 */
const CruzamentosDoAnimal = ({ animal, laboratoryId, onClose }) => {
  const [linhas, setLinhas] = useState(null);
  const [erro, setErro] = useState(null);
  const [busca, setBusca] = useState('');

  useEffect(() => {
    let vivo = true;
    listarCruzamentos(animal, laboratoryId)
      .then(r => { if (vivo) setLinhas(r || []); })
      .catch(err => { if (vivo) setErro(describeError(err)); });
    return () => { vivo = false; };
  }, [animal, laboratoryId]);

  const papeis = [ehTouro(animal), ehDoadora(animal), ehReceptora(animal)].filter(Boolean).length;
  const ocultar = papeis !== 1 ? null
    : ehTouro(animal) ? 'touro' : ehDoadora(animal) ? 'doadora' : 'receptora';

  const colunas = [
    { campo: 'doadora', titulo: 'Doadora' },
    { campo: 'touro', titulo: 'Touro' },
    { campo: 'receptora', titulo: 'Receptora' },
  ].filter(c => c.campo !== ocultar);

  const alvo = busca.trim().toLowerCase();
  const visiveis = (linhas || []).filter(l => !alvo ||
    colunas.some(c => (l[c.campo] || '').toLowerCase().includes(alvo)));

  const total = linhas?.length ?? 0;

  return (
    <JanelaModal chave="cruzamentos" titulo={`Cruzamentos · ${animal.identifier}`}
      subtitulo={linhas ? `${total} transferência${total !== 1 ? 's' : ''}` : null}
      onFechar={onClose} largura="sm:max-w-3xl">
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {total > 0 && (
          <div className="relative max-w-xs">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              autoFocus
              placeholder={`Buscar por ${colunas.map(c => c.titulo.toLowerCase()).join(', ')}...`}
              value={busca}
              onChange={e => setBusca(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {busca && (
              <button onClick={() => setBusca('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X size={13} />
              </button>
            )}
          </div>
        )}

        {erro ? (
          <p className="py-10 text-center text-sm text-red-600">{MARK.WARN} {erro}</p>
        ) : linhas === null ? (
          <p className="py-10 text-center text-sm text-slate-400">Carregando...</p>
        ) : total === 0 ? (
          <p className="py-10 text-center text-sm text-slate-400">Nenhuma transferência com este animal.</p>
        ) : visiveis.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-400">Nada encontrado com essa busca.</p>
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left">
                  <th className="px-3 py-2 font-medium text-slate-700">Aspiração</th>
                  <th className="px-3 py-2 font-medium text-slate-700">FIV</th>
                  <th className="px-3 py-2 font-medium text-slate-700">TE</th>
                  {colunas.map(c => (
                    <th key={c.campo} className="px-3 py-2 font-medium text-slate-700">{c.titulo}</th>
                  ))}
                  <th className="px-3 py-2 font-medium text-slate-700">Serviço</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visiveis.map(l => (
                  <tr key={l.te_transfer_id}>
                    <td className="px-3 py-2 text-slate-900 whitespace-nowrap">{fmt(l.aspiracao)}</td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{fmt(l.fiv)}</td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{fmt(l.te)}</td>
                    {colunas.map(c => (
                      <td key={c.campo} className="px-3 py-2 text-slate-700">{l[c.campo] || '—'}</td>
                    ))}
                    <td className="px-3 py-2 text-slate-500 text-xs whitespace-nowrap">
                      {l.servico_numero ? `#${l.servico_numero}` : '—'}
                      {l.client_name && <span className="block text-slate-400 truncate max-w-[140px]">{l.client_name}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </JanelaModal>
  );
};

export default CruzamentosDoAnimal;
